import {
	Address,
	AddressFormValues,
	AddressSearchFormValues,
	AddressType,
	AddressWithoutId,
} from '@types';

/**
 * Maps the address object to a formatted string.
 * @param address - The address object to be mapped.
 * @returns The formatted address string.
 */
export function mapAddress(address?: Address | AddressWithoutId): string {
	return [
		address?.addressLine1,
		address?.addressLine2,
		address?.suburb,
		[address?.state, address?.postalCode].filter(str => str && str.length > 0).join(' '),
		!isInAustralia(address) ? address?.country : '',
	]
		.filter(str => str && str.length > 0)
		.join(', ');
}

/**
 * Checks if the given address is in Australia.
 * @param address - The address to check.
 * @returns True if the address is in Australia, false otherwise.
 */
export function isInAustralia(address?: Address | AddressWithoutId) {
	return address?.country?.toLowerCase() == 'australia';
}

/**
 * Checks if an address is empty.
 * An address is considered empty if it is null, undefined, or all of its properties are null or undefined.
 * @param address - The address object to check.
 * @returns True if the address is empty, otherwise false.
 */
export function isEmptyAddress(address?: Address | AddressWithoutId | null | unknown): boolean {
	if (!address) {
		return true;
	}
	return !Object.values(new Object(address)).some(value => value != null && value != undefined);
}

export function getAddressSearchInitialValues(
	addressType?: AddressType,
	streetAddress?: Address | AddressFormValues,
	postalAddress?: Address | AddressFormValues,
): AddressSearchFormValues {
	return {
		address: getAddressByType(addressType, streetAddress, postalAddress),
		isOverwrittingPostalAddress: false,
	};
}

export function getAddressByType(
	addressType?: AddressType,
	streetAddress?: Address | AddressFormValues,
	postalAddress?: Address | AddressFormValues,
) {
	return AddressType.STREET == addressType
		? streetAddress
		: AddressType.POSTAL == addressType
			? postalAddress
			: undefined;
}
